import { useAction, useAtom } from '@reatom/npm-react'
import { Reorder } from 'motion/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { useMemo } from 'use-memo-one'

import { Array, AsyncStateN } from '@blade/std-lib'
import { AttentionIcon, useBorderBoxSize, WalletIcon } from '@blade/ui-kit'
import { startNewWalletFlowEvent } from '@root/entities/wallet'
import { reorderWalletsAction } from '@root/entities/wallet/atoms/reatom-wallets-reorder'
import { createColumnGridTemplate, InfoMessage } from '@root/shared/ui-kit'
import { DataTableHeader } from '@root/shared/ui-kit/data-table/ui/data-table-header'
import { InstantTradeWalletsButton } from '@root/widgets/instant-trade-dialog/ui/instant-trade-wallets/instant-trade-wallets-button'

import {
  reatomUserWalletsTable,
  type WalletsTableColumnId,
} from '../atoms/reatom-user-wallets-table'

import { WalletsTableRow } from './wallets-table-row'
import { WalletsTableSkeleton } from './wallets-table-skeleton'

import type { WalletId } from '@root/entities/wallet'
import type { WalletModel } from '@root/entities/wallet/atoms'

type WalletsTableProps = {
  onTransactionsClick: (wallet: WalletModel) => void
  onEditClick: (wallet: WalletModel) => void
  selectedWalletIds: Set<WalletId>
  onToggleSelection: (wallet: WalletModel) => void
  onSelectSingleWallet: (wallet: WalletModel) => void
  withSelection?: boolean
  withTransactions?: boolean
}

export const WalletsTable: React.FC<WalletsTableProps> = ({
  onTransactionsClick,
  onEditClick,
  selectedWalletIds,
  onToggleSelection,
  onSelectSingleWallet,
  withSelection = true,
  withTransactions = true,
}) => {
  const { t } = useTranslation()
  const tableModel = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomUserWalletsTable({ withSelection, withTransactions }),
    [withSelection, withTransactions],
  )
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | undefined>(undefined)
  const bodyHeight = useBorderBoxSize(bodyEl).blockSize
  const reorderWallets = useAction(reorderWalletsAction)
  const [columns] = useAtom(tableModel.columns)
  const [walletsState] = useAtom(tableModel)
  const [expandedWallets] = useAtom(tableModel.expandedWallets)
  const toggleExpandedWallet = useAction(tableModel.toggleExpandedWallet)
  const clearExpandedWallets = useAction(tableModel.clearExpandedWallets)
  const startNewWalletFlow = useAction(startNewWalletFlowEvent)
  const retry = useAction(tableModel.retry)
  const [walletOrder, setWalletOrder] = useState<Array<WalletId>>([])

  const rowStyle = useMemo(
    (): React.CSSProperties => ({
      gridTemplateColumns: createColumnGridTemplate(columns),
    }),
    [columns],
  )

  const handleReorder = () => {
    reorderWallets({ walletIds: walletOrder })
  }

  const hasExpandedWallets = expandedWallets.size > 0
  const noItems = AsyncStateN.matchWaiting(walletsState, {
    Success: (data) => Array.isEmpty(data.data),
    Error: () => true,
    Waiting: () => false,
  })

  const orderedWallets = useMemo((): Array<WalletModel> => {
    const wallets = AsyncStateN.matchWaiting(walletsState, {
      Success: (data) => data.data,
      Error: (): Array<WalletModel> => [],
      Waiting: (): Array<WalletModel> => [],
    })

    const byId = new Map(wallets.map((w) => [w.id, w]))
    const seen = new Set<WalletId>()
    const result: Array<WalletModel> = []

    for (const id of walletOrder) {
      const wallet = byId.get(id)
      if (wallet) {
        result.push(wallet)
        seen.add(id)
      }
    }
    for (const wallet of wallets) {
      if (!seen.has(wallet.id)) {
        result.push(wallet)
      }
    }

    return result
  }, [walletOrder, walletsState])

  const orderedWalletIds = useMemo(
    () => orderedWallets.map((w) => w.id),
    [orderedWallets],
  )

  const handleCreateFirstWallet = () => startNewWalletFlow()

  // TODO: when no items, show the create first wallet button bypassing the AsyncStateN.matchWaiting
  return (
    <Root>
      <TableHeader>
        <DataTableHeader<WalletModel, WalletsTableColumnId>
          model={tableModel}
          columns={columns}
          context={undefined}
        />
      </TableHeader>
      <Rows
        data-empty={noItems}
        data-has-expanded={hasExpandedWallets}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            clearExpandedWallets()
          }
        }}
        ref={(el) => {
          setBodyEl(el ?? undefined)
        }}
      >
        {AsyncStateN.matchWaiting(walletsState, {
          Error: () => (
            <InfoMessage
              intent="error"
              variant="dt"
              icon={<AttentionIcon />}
              title={t('wallet.list-wallets.error.title')}
              description={t('wallet.list-wallets.error.description')}
              withSeparator
              onRetry={retry}
            />
          ),
          Waiting: () => (
            <>
              {Array.range(1, 4).map((it) => (
                <WalletsTableSkeleton key={it} />
              ))}
            </>
          ),
          Success: (data) => {
            if (Array.isEmpty(data.data)) {
              return (
                <InfoMessage
                  intent="empty"
                  variant="dt"
                  icon={<WalletIcon />}
                  title={t('wallet.instant_dialog.no_wallets')}
                  description={t('wallet.instant_dialog.open_first')}
                  css={css`
                    margin-top: 12px;
                  `}
                  withSeparator
                >
                  <InstantTradeWalletsButton
                    onCreateWallet={handleCreateFirstWallet}
                  />
                </InfoMessage>
              )
            }

            return (
              <Reorder.Group
                as="div"
                axis="y"
                values={orderedWalletIds}
                onReorder={setWalletOrder}
                style={reorderGroupStyle}
              >
                {orderedWallets.map((wallet) => (
                  <WalletsTableRow
                    bodyHeight={bodyHeight}
                    style={rowStyle}
                    key={wallet.id}
                    wallet={wallet}
                    expanded={expandedWallets.has(wallet)}
                    onExpandToggle={() => toggleExpandedWallet(wallet)}
                    selected={selectedWalletIds.has(wallet.id)}
                    onToggleSelection={() => onToggleSelection(wallet)}
                    onSelectWallet={() => onSelectSingleWallet(wallet)}
                    onTransactionsClick={onTransactionsClick}
                    onEditClick={onEditClick}
                    onDragEnd={handleReorder}
                    faded={hasExpandedWallets && !expandedWallets.has(wallet)}
                    withSelection={withSelection}
                    withTransactions={withTransactions}
                  />
                ))}
              </Reorder.Group>
            )
          },
        })}
      </Rows>
    </Root>
  )
}

const Root = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
  overflow: auto;
`

const Rows = styled.div`
  background: var(--background-1);
  display: grid;
  grid-auto-flow: row;
  overflow-y: auto;
  min-height: 0;
  align-content: start;

  &[data-has-expanded='true'] {
    overflow-y: hidden;
  }
  &[data-empty='true'] {
    align-content: center;
  }
`

const reorderGroupStyle: React.CSSProperties = { display: 'contents' }

const TableHeader = styled.div`
  [data-table-header] > div:nth-child(1) {
    padding-inline: 14px;
  }
  [data-table-header] > div:nth-child(2) {
    padding-inline: 10px;
  }
`
